import React from 'react';
import EUUKConsumerRights from '../components/marketing/EUUKConsumerRights';

/**
 * Compliance surface — Paddle requires reachable terms plus a cancellation/refund policy.
 *
 * Ported from the marketing site and corrected for the browser app: sections 2, 5, 6, 11 and 18
 * previously described the retired desktop product, most seriously section 18's claim that
 * "CensorMe does not store or process audio files. We only receive the local system path of your
 * audio file." The SPA uploads media and stores derived transcripts and XML in R2, so those
 * statements were wrong. The billing, arbitration and consumer-rights sections are untouched.
 *
 * Still needs owner/counsel sign-off, and section 10 links to a /privacy page that does not exist
 * yet — see docs/BACKEND_REQUESTS.md §11.
 */
const TermsOfServicePage: React.FC = () => {
  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8 w-full">
      <div className="prose prose-invert max-w-none">
        <h1 className="text-4xl font-bold text-white mb-2">Terms of Service for CensorMe</h1>
        <p className="text-gray-400 mb-8">Last Updated: July 31, 2026</p>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
          <p className="text-gray-300 leading-relaxed">
                        By accessing or using CensorMe (the &quot;Service&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree, you must not use the Service.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
          <p className="text-gray-300 mb-4">
                        CensorMe is a browser-based audio processing service. When you use it, we:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
            <li>Receive the media files you upload, and store them on our servers for as long as is needed to process them</li>
            <li>Transcribe that media into a timestamped, word-level transcript, which we store in your account</li>
            <li>Compare the transcript against the word lists you choose, matching case-insensitively</li>
            <li>Generate a Final Cut Pro XML (xmeml) timeline in which the matching words are cut out or silenced, and store it in your account so you can download it again</li>
          </ul>
          <p className="text-gray-300 leading-relaxed">
                        CensorMe does not alter your original media. It produces a timeline that you
                        import into your own editing software, where every cut can be reviewed, changed or undone.
                        You may delete your uploads, transcripts and generated timelines from your account at any
                        time.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>You must create an account to use the Service</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials</li>
            <li>You are fully responsible for all activities that occur under your account</li>
            <li>You agree to provide accurate, current, and complete information during registration and to keep it updated</li>
            <li>You must notify us immediately of any unauthorized use of your account</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">4. Acceptable Use</h2>
          <p className="text-gray-300 mb-4">You agree not to use the Service to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>Violate any applicable law or regulation</li>
            <li>Engage in harassment, defamation, or threatening behavior</li>
            <li>Upload or transmit viruses, malware, or any malicious code</li>
            <li>Attempt to gain unauthorized access to the Service or its systems</li>
            <li>Reverse engineer, decompile, or extract source code or algorithms</li>
            <li>Infringe intellectual property or privacy rights of others</li>
            <li>Impose an unreasonable or disproportionately large load on our infrastructure</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">5. Content and Intellectual Property</h2>
          <h3 className="text-xl font-semibold text-white mb-3">Your Content</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-6">
            <li>You retain ownership of all content you upload</li>
            <li>You grant CensorMe a worldwide, non-exclusive, royalty-free license to process, store, and transmit your content solely as necessary to provide the Service</li>
            <li>You represent that you have all necessary rights to upload and process the content</li>
            <li>Uploaded media is retained only as long as is needed to transcribe it. The transcripts and generated timelines derived from it are stored in your account until you delete them or close your account</li>
          </ul>
          <h3 className="text-xl font-semibold text-white mb-3">Our Content</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>All software, algorithms, interfaces, documentation, and trademarks are the exclusive property of CensorMe</li>
            <li>You may not copy, modify, distribute, or create derivative works of our content without prior written consent</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">6. Limitations of Service</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>Detection accuracy is not guaranteed and depends on audio quality, accents, background noise, and context</li>
            <li>You must manually review all suggested edits before publishing</li>
            <li>Output is a Final Cut Pro XML (xmeml) timeline. Compatibility is limited to editing software that imports that format, which includes supported versions of Adobe Premiere Pro, DaVinci Resolve and Final Cut Pro</li>
            <li>You are responsible for setting a frame rate that matches your sequence; a mismatch will put the edits out of sync</li>
            <li>Processing times may vary based on file length and server load</li>
            <li>The Service requires an internet connection; it does not run offline</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">
                        7. Subscriptions, Payments, Cancellation &amp; Refunds
          </h2>

          <h3 className="text-xl font-semibold text-white mb-3">Subscription Plans</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-6">
            <li>CensorMe is a paid subscription service; new accounts begin with a trial and require
                            valid payment details up front</li>
            <li>The Pro tier allows 4 hours per file and 25 hours of processing per month, with
                            priority support</li>
            <li>If a subscription is cancelled, expires, or is paused, Pro limits no longer apply and
                            processing is restricted until it is reactivated</li>
            <li>Payments are securely processed through our payment processor, Paddle</li>
            <li>All fees are exclusive of applicable taxes (e.g., VAT, sales tax)</li>
          </ul>

          <h3 className="text-xl font-semibold text-white mb-3">Automatic Renewal</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
                        Paid Subscriptions automatically renew until cancelled. We&apos;ll notify you if the
                        price of a Paid Subscription increases and, if required, seek your consent to continue.
                        You&apos;ll be charged between 00:00 and 01:00 (UTC) on the day the Paid Subscription
                        period renews.
          </p>

          <h3 className="text-xl font-semibold text-white mb-3">Cancellation</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
                        If you wish to cancel your subscription, please contact us at{' '}
            <a href="https://paddle.net/" target="_blank" rel="noreferrer" className="text-blue-400 underline">
                            https://paddle.net/
            </a>{' '}
                        at least 48 hours before the end of the current billing period. Please make sure you provide your order number and the email address used to purchase the Product. Your cancellation will take effect at the next payment date.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4">
                        There are no refunds on unused subscription periods, except as required by applicable consumer law (see section 8 below for EU/UK consumer rights).
          </p>

          <h3 className="text-xl font-semibold text-white mb-3">Free Trials</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
                        Certain Paid Subscriptions may offer a free trial before charging you. If you decide to
                        unsubscribe from a Paid Subscription before we start charging your payment method, you
                        must cancel the subscription before the expiration of the trial period by contacting us
                        at{' '}
            <a href="https://paddle.net/" target="_blank" rel="noreferrer" className="text-blue-400 underline">
                            https://paddle.net/
            </a>
                        .
          </p>

          <h3 className="text-xl font-semibold text-white mb-3">Payment Method Issues</h3>
          <p className="text-gray-300 leading-relaxed">
                        If we cannot charge your payment method for any reason (such as expiration or
                        insufficient funds), and you have not cancelled your Paid Subscription, you remain
                        responsible for any uncollected amounts. We reserve the right to cancel your Paid
                        Subscription if we are unable to successfully charge your payment method to renew your
                        subscription.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">
                        8. Consumer Right to Cancel (EU &amp; UK Residents)
          </h2>
          <EUUKConsumerRights />
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">9. User Responsibilities</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>Use the Service only for lawful purposes</li>
            <li>Respect the rights and dignity of others</li>
            <li>Do not upload content containing hate speech, violence, or illegal material</li>
            <li>Review all automated edits before publishing or distributing content</li>
            <li>Comply with all applicable laws regarding content creation and distribution</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">10. Privacy</h2>
          <p className="text-gray-300 leading-relaxed">
                        Your use of the Service is also governed by our Privacy Policy, available at /privacy. We do not sell your personal information and only share it as described in the Privacy Policy or required by law.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">11. Third-Party Services</h2>
          <p className="text-gray-300 mb-4">The Service integrates with:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>Paddle (payment processing and merchant of record)</li>
            <li>Supabase (authentication and database)</li>
            <li>Cloudflare (hosting, CDN, and file storage)</li>
            <li>Automated speech recognition providers (transcription of uploaded media)</li>
            <li>Video Editing Software which accept Final Cut Pro XMEML imports (timeline export)</li>
          </ul>
          <p className="text-gray-300">Your use of these services is subject to their respective terms and privacy policies.</p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">12. Termination</h2>
          <p className="text-gray-300 mb-4">We may suspend or terminate your account immediately if you:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
            <li>Violate these Terms</li>
            <li>Engage in illegal or abusive behavior</li>
            <li>Impose excessive load on our systems</li>
          </ul>
          <p className="text-gray-300">You may terminate your account at any time by contacting support@censorme.app.</p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">13. Limitation of Liability</h2>
          <p className="text-gray-300 uppercase font-semibold mb-4">To the maximum extent permitted by law:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>The Service is provided &quot;AS IS&quot; without warranties of any kind</li>
            <li>We are not liable for indirect, incidental, special, consequential, or punitive damages</li>
            <li>Our total liability shall not exceed the amount you paid us in the 12 months prior to the claim</li>
            <li>We are not responsible for lost profits, data loss, or business interruption</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">14. Indemnification</h2>
          <p className="text-gray-300 leading-relaxed">
                        You agree to defend, indemnify, and hold harmless CensorMe and its officers, directors,
                        employees, and agents from any claims, damages, losses, liabilities, costs, and expenses
                        (including reasonable attorneys&rsquo; fees) arising from your use of the Service, your
                        uploaded content, or your violation of these Terms.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">15. Binding Arbitration and Class Action Waiver</h2>
          <p className="text-gray-300 mb-4">
                        Any dispute arising out of or relating to these Terms or the Service shall be resolved through binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules.
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li>You waive the right to participate in a class action lawsuit or class-wide arbitration</li>
            <li>Arbitration will be conducted on an individual basis only</li>
            <li>This provision survives termination of these Terms</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">16. Governing Law and Jurisdiction</h2>
          <p className="text-gray-300 leading-relaxed">
                        These Terms are governed by the laws of the State of Delaware, USA, without regard to
                        conflict of law principles. Any legal action not subject to arbitration shall be brought
                        exclusively in the state or federal courts located in Delaware.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">17. Miscellaneous</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
            <li><strong>Force Majeure:</strong> We are not liable for delays or failures caused by events beyond our reasonable control</li>
            <li><strong>No Waiver:</strong> Our failure to enforce any right does not constitute a waiver</li>
            <li><strong>Severability:</strong> If any provision is unenforceable, the remainder remains in effect</li>
            <li><strong>Entire Agreement:</strong> These Terms and our Privacy Policy constitute the entire agreement between you and CensorMe</li>
            <li><strong>Export Compliance:</strong> You may not use the Service in violation of U.S. export laws or if you are located in an embargoed country</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">18. DMCA / Copyright Complaints</h2>
          <p className="text-gray-300 leading-relaxed mb-4">
                        CensorMe stores and processes the media that users upload, together with the transcripts
                        and timelines derived from it. That content is private to the account that uploaded it
                        and is not published by us, but we respond to valid notices of claimed infringement.
          </p>
          <p className="text-gray-300 leading-relaxed mb-4">
                        If you believe content processed through the Service infringes your copyright, send a
                        notice to <span className="text-white">dmca@censorme.app</span> including:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
            <li>Identification of the copyrighted work you claim has been infringed</li>
            <li>Identification of the material you claim is infringing, with enough detail for us to locate it</li>
            <li>Your name, address, telephone number and email address</li>
            <li>A statement that you have a good-faith belief the use is not authorized by the copyright owner, its agent, or the law</li>
            <li>A statement, under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or authorized to act on their behalf</li>
            <li>Your physical or electronic signature</li>
          </ul>
          <p className="text-gray-300 leading-relaxed">
                        We will remove or disable access to material that is the subject of a valid notice and may
                        terminate the accounts of repeat infringers.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-4">19. Contact Information</h2>
          <p className="text-gray-300">For any questions about these Terms, contact us at:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300 ml-6">
            <li>Email: admin@censorme.app</li>
            <li>Support: support@censorme.app</li>
          </ul>
        </section>

        <section className="mb-12 pt-8 border-t border-gray-700">
          <p className="text-gray-300 italic text-center">
                        By using CensorMe, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
